/*
 * Navigation rail state. On a desktop viewport the rail is either expanded or
 * "mini" (icons, with each group's links in a hover flyout); mini is persisted
 * in localStorage so the layout's head boot script can stamp
 * <html data-mcp-rail="mini"> before first paint and nothing jumps. On a
 * narrow viewport the rail is a drawer over the page; open/closed is never
 * persisted. The CSS lives in console.css (section 2).
 */
const STORAGE_KEY = 'mcp-rail';
const MINI_ATTR = 'data-mcp-rail';
const OPEN_ATTR = 'data-mcp-rail-open';
const NARROW = '(max-width: 991.98px)';

export function isNarrow(): boolean {
    return typeof window.matchMedia === 'function' ? window.matchMedia(NARROW).matches : false;
}

export function isMini(): boolean {
    return document.documentElement.getAttribute(MINI_ATTR) === 'mini';
}

export function setMini(mini: boolean): void {
    if (mini) document.documentElement.setAttribute(MINI_ATTR, 'mini');
    else document.documentElement.removeAttribute(MINI_ATTR);
    try {
        if (mini) localStorage.setItem(STORAGE_KEY, 'mini');
        else localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage unavailable: the choice lasts for the page */ }
}

export function isOpen(): boolean {
    return document.documentElement.hasAttribute(OPEN_ATTR);
}

export function openRail(): void {
    document.documentElement.setAttribute(OPEN_ATTR, '');
}

export function closeRail(): void {
    document.documentElement.removeAttribute(OPEN_ATTR);
}

/** The top bar's toggle: drawer on a narrow screen, mini/expanded otherwise. */
export function toggleRail(): false {
    if (isNarrow()) {
        if (isOpen()) closeRail(); else openRail();
    } else {
        setMini(!isMini());
    }
    return false;
}

let bound = false;

export function initRail(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && isOpen()) closeRail();
    });
    // In mini mode a group heading is an icon whose links show on hover; on a
    // touch device there is no hover, so tapping it brings the full rail back.
    document.addEventListener('click', event => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('.mcp-nav-heading') && isMini() && !isNarrow()) setMini(false);
    });
}

/** Test support. */
export function resetRail(): void {
    document.documentElement.removeAttribute(MINI_ATTR);
    document.documentElement.removeAttribute(OPEN_ATTR);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
